// Kits.jsx - Sending kits, in bulk. The queue of people waiting for a mailer, and
// the sheet that comes out of the printer.
//
// The thing this screen exists to prevent: at a hundred kits a day the labels are
// identical grey rectangles, and the return label for one customer ends up inside
// somebody else's mailer. So the two labels of a kit are bought in one action and
// printed in two runs that stay in the same order, the return labels first and
// the outside labels second, with the order number above every one of them.
//
// Nothing is asked before buying. No insurance value, no service picker, no
// dialog. The address is on the order and everything else is in settings.
//
// LEGEND
//   1. Kits          the queue, selection, and the buy
//   2. KitSheet      the print run, one dialog per document
//   3. LabelPage     one label at full size on its own sheet
//   4. Confirmation  the optional record of what was printed

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import clsx from "clsx";

import { useAuth } from "./AdminApp.jsx";
import { useAsync } from "../lib/hooks.js";
import { ordersApi } from "../lib/api.js";
import { ago, dayTime } from "../lib/format.js";
import { Panel, Button, Loading, Alert, Empty, Tag, Checkbox, useToast } from "../ui/kit.jsx";
import { Truck, Printer, Check, ArrowRight } from "../ui/icons.jsx";

// The server takes twenty five at a time. Bigger selections are split, so a
// morning of two hundred is a progress number rather than a request that dies.
const CHUNK = 25;

const chunked = (list) =>
  Array.from({ length: Math.ceil(list.length / CHUNK) }, (_, index) => list.slice(index * CHUNK, index * CHUNK + CHUNK));

////////// 1. THE QUEUE //////////

export default function Kits() {
  const toast = useToast();
  const [tab, setTab] = useState("waiting");
  const [picked, setPicked] = useState(() => new Set());
  const [progress, setProgress] = useState(null);
  const [done, setDone] = useState(null);

  // Waiting is oldest first, because the person who has waited longest for a
  // mailer should be in the first batch. Bought is newest first, because that
  // list is only ever read to reprint something from today.
  const queue = useAsync(
    (signal) => ordersApi.list(
      tab === "waiting"
        ? { kit: "waiting", limit: 200, sort: "createdAt", dir: "asc" }
        : { kit: "bought", limit: 200, sort: "updatedAt", dir: "desc" },
      signal
    ),
    [tab]
  );

  const rows = queue.data?.items || [];
  // A row can only be bought if we can address it. Saying so in the list beats
  // twenty five failures in a row after the button is pressed.
  const buyable = useMemo(() => rows.filter((row) => row.city && row.state), [rows]);

  useEffect(() => { setPicked(new Set()); setDone(null); }, [tab]);

  const toggle = (number) => setPicked((previous) => {
    const next = new Set(previous);
    if (next.has(number)) next.delete(number); else next.add(number);
    return next;
  });

  const allPicked = buyable.length > 0 && buyable.every((row) => picked.has(row.number));
  const selectAll = () => setPicked(allPicked ? new Set() : new Set(buyable.map((row) => row.number)));

  const buy = async () => {
    const numbers = [...picked];
    const results = [];
    setDone(null);

    for (const batch of chunked(numbers)) {
      setProgress({ done: results.length, total: numbers.length });
      try {
        const answer = await ordersApi.buyKits(batch);
        results.push(...answer.results);
      } catch (error) {
        results.push(...batch.map((number) => ({ number, ok: false, reason: error.message })));
      }
    }

    setProgress(null);
    setPicked(new Set());
    queue.reload();

    const ok = results.filter((result) => result.ok);
    setDone({ ok, failed: results.filter((result) => !result.ok) });
    if (ok.length) toast.success(`${ok.length} kit${ok.length === 1 ? "" : "s"} ready to print`);
    else toast.error("Nothing was bought");
  };

  // Printing is its own press on purpose. A tab opened from a real click is the
  // only kind a popup blocker allows, and people press it when they are standing
  // at the printer rather than at the moment they bought.
  const printThem = (numbers) => window.open(`/admin/kits/print?numbers=${numbers.join(",")}`, "_blank", "noopener");

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px]">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="display display-lg text-ink">Kits</h1>
          <p className="hint mt-1">Both labels in one press. They print in two runs, return labels first, in the same order.</p>
        </div>

        <div className="chip-row">
          {[["waiting", "Waiting"], ["bought", "Labels bought"]].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={clsx("btn btn-sm", tab === key ? "btn-ink" : "btn-quiet")}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      {done && (
        <Panel className="p-4 mb-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="flex items-center gap-2 text-[14px]">
              <Check size={16} className="text-bronze shrink-0" />
              <span className="font-semibold text-ink">
                {done.ok.length} kit{done.ok.length === 1 ? "" : "s"} bought.
              </span>
              <span className="text-muted">Print the pairs before you pack anything.</span>
            </p>
            {done.ok.length > 0 && (
              <Button onClick={() => printThem(done.ok.map((result) => result.number))}>
                <Printer size={15} /> Print {done.ok.length} pair{done.ok.length === 1 ? "" : "s"}
              </Button>
            )}
          </div>

          {done.failed.length > 0 && (
            <Alert tone="error" className="mt-3" title={`${done.failed.length} could not be bought`}>
              <ul className="space-y-1 mt-1">
                {done.failed.map((result) => (
                  <li key={result.number} className="text-[13px]">
                    <Link to={`/admin/orders/${result.number}`} className="num font-semibold underline">{result.number}</Link>
                    <span> {result.reason}</span>
                  </li>
                ))}
              </ul>
            </Alert>
          )}
        </Panel>
      )}

      {tab === "waiting" && (
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <Button variant="quiet" size="sm" onClick={selectAll} disabled={!buyable.length}>
            {allPicked ? "Clear the selection" : `Select all ${buyable.length}`}
          </Button>
          <Button onClick={buy} disabled={!picked.size} loading={Boolean(progress)}>
            <Truck size={15} /> Buy {picked.size || ""} kit{picked.size === 1 ? "" : "s"}
          </Button>
          {progress && <span className="hint num">{progress.done} of {progress.total} done</span>}
        </div>
      )}

      {queue.error && <Alert tone="error" className="mb-4">{queue.error.message}</Alert>}

      <Panel className="overflow-hidden max-sm:border-0 max-sm:bg-transparent">
        {queue.loading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty
            icon={Truck}
            title={tab === "waiting" ? "No kits waiting" : "No labels bought yet"}
            action={<Link to="/admin/orders?status=new,vetting" className="btn btn-quiet btn-sm">Review new requests <ArrowRight size={14} /></Link>}
          >
            {tab === "waiting"
              ? "Every approved order already has its labels. Approving a request puts it here."
              : "Kits with labels appear here, so a pair can be printed again."}
          </Empty>
        ) : (
          <div className="sm:overflow-x-auto">
            <table className="grid-table stack-table w-full sm:min-w-[760px]">
              <thead>
                <tr>
                  {tab === "waiting" && <th className="w-9" />}
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Going to</th>
                  <th>{tab === "waiting" ? "Approved" : "Bought"}</th>
                  <th className="text-right" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const addressable = Boolean(row.city && row.state);
                  return (
                    <tr key={row.number} className={clsx("group", picked.has(row.number) && "bg-surface")}>
                      {tab === "waiting" && (
                        <td data-label="">
                          <Checkbox
                            checked={picked.has(row.number)}
                            onChange={() => toggle(row.number)}
                            disabled={!addressable}
                            aria-label={`Select ${row.number}`}
                          />
                        </td>
                      )}
                      <td className="num" data-label="Order">
                        <Link to={`/admin/orders/${row.number}`} className="font-semibold text-ink hover:text-bronze-dark">
                          {row.number}
                        </Link>
                      </td>
                      <td data-label="Customer">
                        <span className="text-ink">{row.name || "—"}</span>
                        {!addressable && <Tag tone="critical" className="ml-2">No address</Tag>}
                      </td>
                      <td className="text-muted whitespace-nowrap stack-hide" data-label="Going to">
                        {addressable ? `${row.city}, ${row.state}` : "—"}
                      </td>
                      <td className="text-[12.5px] text-faint whitespace-nowrap" data-label="When">
                        {ago(tab === "waiting" ? row.createdAt : row.kit?.at || row.updatedAt)}
                      </td>
                      <td className="text-right" data-label="">
                        {tab === "bought" && (
                          <Button size="sm" variant="quiet" onClick={() => printThem([row.number])}>
                            <Printer size={14} /> Pair
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

////////// 2. THE PRINT RUN //////////
// Not one page with two labels on it. Two print dialogs, in the order the bench
// works in: the return labels first, because those go inside the mailer, then the
// labels that go on the outside. Each label prints at its real size, four by six
// inches, so it comes off a laser printer ready to peel or tape without anybody
// resizing anything.
//
// A batch prints as two stacks in the same sequence rather than two dialogs per
// order, because forty dialogs is not a workflow. Both stacks are in the order
// the queue showed, and every page carries the order number above the label, so a
// stack that gets knocked off a bench can be put back together.
//
// The third document is off unless somebody turned it on in settings: the two
// tracking numbers and who printed them, for the file.

const STEPS = [
  { key: "inbound", title: "Return labels", note: "Go inside the mailer" },
  { key: "outbound", title: "Outside labels", note: "Go on the mailer" },
  { key: "confirmation", title: "Confirmation sheet", note: "For the file" },
];

export function KitSheet() {
  const [params] = useSearchParams();
  const numbers = useMemo(
    () => String(params.get("numbers") || "").split(",").map((entry) => entry.trim()).filter(Boolean),
    [params]
  );

  const key = numbers.join(",");
  const sheets = useAsync((signal) => ordersApi.kitSheet(numbers, signal), [key], { skip: !numbers.length });
  // Memoised so its identity is stable: it is a dependency of the step list
  // below, and `|| []` would otherwise be a fresh array on every render.
  const pages = useMemo(() => sheets.data?.sheets || [], [sheets.data]);
  // A step only exists if it has something to put on paper. The kit going out is
  // First Class Mail, and without Stamps.com connected there is no label image
  // for it at all: somebody buys that postage on stamps.com by hand. Offering the
  // step anyway would send a stack of blank 4 by 6 sheets through the printer.
  const steps = useMemo(() => STEPS.filter((step) => {
    if (step.key === "confirmation") return Boolean(sheets.data?.confirmation);
    return pages.some((page) => (step.key === "inbound" ? page.inbound : page.outbound)?.label);
  }), [sheets.data, pages]);

  // Printing before the images arrive prints empty boxes, so nothing starts
  // until every label has landed or failed.
  const expected = pages.reduce(
    (total, page) => total + (page.outbound?.label ? 1 : 0) + (page.inbound?.label ? 1 : 0),
    0
  );
  const [settled, setSettled] = useState(0);
  const ready = pages.length > 0 && settled >= expected;

  // Which document is in front of the print dialog right now, and how far the
  // run has got. "waiting" means the browser has not been asked yet.
  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState("waiting");
  const [done, setDone] = useState(() => new Set());

  // Which of the three documents is in the print area. Declared up here because
  // the effect below reads it, and everything below the early returns is too
  // late for a hook.
  const current = steps[step]?.key;

  // `@page` is the one thing CSS will not let a class decide, and the two
  // documents want different paper: a label is its own 4 by 6 sheet, the
  // confirmation is a letter page for the file. So the rule for whichever one is
  // about to print is written into its own style element and taken away again.
  //
  // The size matters more than it looks. A 4 by 6 label laid out on a letter
  // page prints correctly and wastes most of a sheet, and on a label printer it
  // comes out shrunk into a corner.
  useEffect(() => {
    const rule = document.createElement("style");
    rule.textContent = current === "confirmation"
      ? "@page { size: letter portrait; margin: 0.35in; }"
      : "@page { size: 4in 6in; margin: 0; }";
    document.head.appendChild(rule);
    return () => rule.remove();
  }, [current]);

  const print = useCallback((index) => {
    setStep(index);
    setPhase("printing");
    // A frame, so the swapped document is laid out before the dialog snapshots it.
    requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
  }, []);

  // The browser tells us when the dialog closes, whether it printed or was
  // cancelled. Either way that document has had its turn, and the next one is
  // offered rather than forced: a cancel usually means somebody wants to fix the
  // printer first.
  useEffect(() => {
    const after = () => {
      setDone((previous) => new Set(previous).add(step));
      setPhase((current) => (current === "printing" ? "between" : current));
    };
    window.addEventListener("afterprint", after);
    return () => window.removeEventListener("afterprint", after);
  }, [step]);

  // The first document goes as soon as the labels are on screen. Everything
  // after it waits half a second, which is long enough for one dialog to close
  // before the next opens and short enough that nobody reaches for the mouse.
  useEffect(() => {
    if (!ready || phase === "printing") return undefined;

    if (phase === "waiting") {
      const timer = setTimeout(() => print(0), 250);
      return () => clearTimeout(timer);
    }

    if (phase === "between" && step + 1 < steps.length) {
      const timer = setTimeout(() => print(step + 1), 600);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [ready, phase, step, steps.length, print]);

  // Logged once, when the last document has been through the dialog. This is the
  // record that the paper exists, which is the thing a photograph of a mailer
  // cannot prove three weeks later.
  const finished = phase === "between" && step + 1 >= steps.length;
  useEffect(() => {
    if (!finished) return;
    ordersApi.markKitsPrinted(pages.map((page) => page.number)).catch(() => {});
  }, [finished, pages]);

  if (!numbers.length) return <Alert tone="error" className="m-6" title="Nothing to print">Open this from the kit queue.</Alert>;
  if (sheets.loading) return <Loading label="Fetching the labels" className="min-h-screen" />;
  if (sheets.error) return <Alert tone="error" className="m-6" title="Could not load the labels">{sheets.error.message}</Alert>;

  return (
    <div className="kit-print">
      {/* Everything in here is screen only. What reaches the paper is one
          document at a time, below. */}
      <div className="no-print p-4 sm:p-6 border-b border-rule">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-[15px] text-ink">
              {pages.length} kit{pages.length === 1 ? "" : "s"}, {steps.length} print{steps.length === 1 ? "" : "s"}
            </p>
            <p className="hint mt-0.5">
              {finished
                ? "All done. Every label has been through the printer and the orders have been logged."
                : ready
                  ? "Each document opens its own print dialog. Keep both stacks in this order and they stay paired."
                  : "Waiting for the labels to load."}
            </p>
          </div>
          <Link to="/admin/kits" className="btn btn-quiet btn-sm">Back to the queue</Link>
        </div>

        <ol className="mt-4 grid gap-2 sm:grid-cols-3">
          {steps.map((entry, index) => (
            <li key={entry.key}>
              <button
                type="button"
                onClick={() => print(index)}
                disabled={!ready}
                className={clsx(
                  "w-full text-left p-3 border transition-colors",
                  index === step && phase === "printing" ? "border-bronze bg-bronze-wash"
                    : done.has(index) ? "border-rule bg-surface"
                      : "border-rule hover:border-faint"
                )}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="text-[13.5px] font-semibold text-ink">{index + 1}. {entry.title}</span>
                  {done.has(index)
                    ? <Check size={15} className="text-bronze shrink-0" />
                    : <Printer size={15} className="text-faint shrink-0" />}
                </span>
                <span className="block hint mt-0.5">
                  {index === step && phase === "printing" ? "Printing now" : done.has(index) ? "Printed. Click to print again" : entry.note}
                </span>
              </button>
            </li>
          ))}
        </ol>
      </div>

      {/* One document in the print area at a time. The others are not hidden with
          CSS, they are not rendered, so a browser cannot decide to print them. */}
      {current === "confirmation"
        ? pages.map((page) => <Confirmation key={page.number} page={page} count={pages.length} />)
        : pages.map((page) => <LabelPage key={page.number} page={page} leg={current} />)}

      {/* Every label of every order, loaded once off screen. This is the only
          thing that counts towards ready: the visible pages show whichever leg is
          in front of the dialog, so counting those would start the run when half
          the images had arrived. It unmounts once they are in, and the second
          document draws them from cache. */}
      {!ready && (
        <div className="sr-only" aria-hidden="true">
          {pages.map((page) => [page.outbound, page.inbound].filter((leg) => leg?.label).map((leg) => (
            <img
              key={leg.tracking}
              src={leg.label}
              alt=""
              onLoad={() => setSettled((count) => count + 1)}
              onError={() => setSettled((count) => count + 1)}
            />
          )))}
        </div>
      )}
    </div>
  );
}

////////// 3. ONE LABEL, ONE PAGE //////////
// Four by six inches, which is the size the carrier made it, on its own sheet.
// The caption above it is the only thing we add: the order number, so a label
// that ends up face down on a bench can still be matched to its mailer.

function LabelPage({ page, leg }) {
  const shipment = leg === "inbound" ? page.inbound : page.outbound;
  const caption = leg === "inbound" ? "Goes INSIDE the mailer" : "Goes ON the mailer";

  // Nothing to print for this one. A leg bought by hand has no label image, and a
  // blank 4 by 6 sheet in the middle of a stack is worse than a shorter stack:
  // it puts every label after it against the wrong mailer.
  if (shipment?.byHand) return null;

  return (
    <section className="label-page">
      <header className="label-head">
        <span className="num label-order">{page.number}</span>
        <span className="label-where">{caption}</span>
        <span className="label-name">{page.name}</span>
      </header>

      {shipment?.label ? (
        <img className="label-image" src={shipment.label} alt={`${caption} for ${page.number}`} />
      ) : (
        <p className="label-missing">
          {shipment
            ? "The label image has expired. Void this leg on the order and buy it again."
            : "No label on this leg yet. Buy it from the order."}
        </p>
      )}

      {shipment?.tracking && <p className="num label-tracking">{shipment.carrier} {shipment.service} · {shipment.tracking}</p>}
    </section>
  );
}

////////// 4. THE CONFIRMATION //////////
// What was printed, for whom, by whom and when. Optional, and off by default.

function Confirmation({ page, count }) {
  const { user } = useAuth();
  const printedAt = new Date();

  return (
    <section className="label-page confirm-page">
      <header className="confirm-head">
        <div>
          <p className="eyebrow">International Gold &amp; Silver</p>
          <p className="confirm-title num">{page.number}</p>
          <p className="confirm-name">{page.name}</p>
          <p className="confirm-where num">{[page.city, page.state].filter(Boolean).join(", ")} {page.postal}</p>
        </div>
        <div className="confirm-meta">
          <p>Labels printed {dayTime(printedAt)}</p>
          <p>By {user?.name || "a member of staff"}</p>
          {count > 1 && <p>One of {count} in this run</p>}
        </div>
      </header>

      <table className="confirm-table">
        <tbody>
          {[["On the mailer", page.outbound], ["Inside the mailer", page.inbound]].map(([label, shipment]) => (
            <tr key={label}>
              <th>{label}</th>
              <td className="num">{shipment?.tracking || "not bought"}</td>
              <td>{shipment ? `${shipment.carrier} ${shipment.service || ""}` : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="confirm-thumbs">
        {[page.outbound, page.inbound].map((shipment, index) => (
          shipment?.label
            ? <img key={index} src={shipment.label} alt="" />
            : <span key={index} className="confirm-blank">No label</span>
        ))}
      </div>

      <p className="confirm-foot">
        This sheet records that both labels were sent to a printer. It is not proof of postage. The carrier
        scan on the tracking number is what proves a package was handed over.
      </p>
    </section>
  );
}
